import {
  DeploymentStrategy,
  UpgradeAssessment,
  UpgradeAssessmentStatus,
  UpgradePlan,
  UpgradePlanStatus,
  UpgradeTemplate,
  UpgradeTemplateStatus,
} from 'api/models';
import { formatDistanceToNowStrict } from 'date-fns';
import { getDateFromUnixTimestamp } from 'utils/formatTime';
import { getHumanReadableDate } from 'utils/subscription_plan';

export const showCancel = (upgr: UpgradeAny) => {
  if (
    upgr.status === 'pending' ||
    upgr.status === 'in-progress' ||
    upgr.status === 'rolled-back' ||
    upgr.status === 'failed' ||
    upgr.status === 'approved' ||
    upgr.status === 'update-in-progress' ||
    upgr.status === 'regenerate-in-progress'
  ) {
    return true;
  } else {
    return false;
  }
};

export const showArchive = (upgr: UpgradeAny) => {
  if (
    upgr.status === 'in-progress' ||
    upgr.status === 'rolled-back' ||
    upgr.status === 'approved' ||
    upgr.status === 'update-in-progress' ||
    upgr.status === 'regenerate-in-progress'
  ) {
    return true;
  } else {
    return false;
  }
};

export const isPlanActionDisabled = (
  upgradeStatus:
    | UpgradePlanStatus
    | UpgradeTemplateStatus
    | UpgradeAssessmentStatus
    | undefined,
) => {
  if (
    !upgradeStatus ||
    upgradeStatus === 'cancelled' ||
    upgradeStatus === 'cancellation-in-progress' ||
    upgradeStatus === 'completed' ||
    upgradeStatus === 'archived' ||
    upgradeStatus === 'failed'
  )
    return true;
  return false;
};

export function getTooltipForUpgradeRequestedBy(
  name: string | undefined,
  date: number | undefined,
  mode: UpgradeViewMode,
): string {
  if (name === undefined || date === undefined) {
    return '';
  }
  const timeAgo = formatDistanceToNowStrict(new Date(date), {
    addSuffix: true,
  });
  const onDate = getHumanReadableDate(date);

  switch (mode) {
    case UpgradeViewMode.Plan:
      return `${name} requested the Upgrade Plan ${timeAgo} (${onDate})`;
    case UpgradeViewMode.Template:
      return `${name} requested the Upgrade Template ${timeAgo} (${onDate})`;
    case UpgradeViewMode.Assessment:
      return `${name} requested the Upgrade Assessment ${timeAgo} (${onDate})`;
  }
}

export function getTooltipForUpgradeCompletedBy(
  name: string | undefined,
  date: number | undefined,
): string {
  if (name === undefined || date === undefined) {
    return '';
  }
  const timeAgo = formatDistanceToNowStrict(new Date(date), {
    addSuffix: true,
  });
  return `${name} marked the Upgrade Plan as Completed ${timeAgo}`;
}

export function getUpgradeTemplateStatusBadge(status: UpgradeTemplateStatus): {
  title: string;
  variant: 'success' | 'error' | 'warning' | 'neutral' | 'brand';
  tooltip?: string;
} {
  switch (status) {
    case 'pending':
      return {
        title: 'Waiting for Template',
        variant: 'neutral',
        tooltip:
          'Upgrade Template generation can take up to 2 days as any missing project coverage has to ' +
          'be added and, at times, preverification uncovers new dependencies / breaking changes. ' +
          'You will be notified when the Upgrade Template is published.',
      };
    case 'in-progress':
      return {
        title: 'Available',
        variant: 'brand',
      };
    case 'available':
      return {
        title: 'Available',
        variant: 'brand',
      };
    case 'completed':
      return {
        title: 'Environment Upgraded',
        variant: 'success',
      };
    case 'cancellation-in-progress':
      return {
        title: 'Cancelation in Progress',
        variant: 'error',
      };
    case 'cancelled':
      return {
        title: 'Canceled',
        variant: 'error',
      };
    case 'outdated':
      return {
        title: 'Outdated',
        variant: 'brand',
      };
    case 'update-in-progress':
      return {
        title: 'Refreshing Template',
        variant: 'brand',
      };
    case 'regenerate-in-progress':
      return {
        title: 'Regenerating Template',
        variant: 'brand',
      };
    case 'approved':
      return {
        title: 'Approved for Use',
        variant: 'success',
      };
    case 'archived':
      return {
        title: 'Archived',
        variant: 'neutral',
      };
  }
}

export const showUpgradeType = (strategy: DeploymentStrategy) => {
  switch (strategy) {
    case 'pick-for-me':
      return 'Pick For Me';
    case 'blue-green':
      return 'Blue Green';
    case 'in-place':
      return 'In Place';
    case 'rolling':
      return 'Rolling';
  }
};

export function getUpgradePlanStatusBadge(status: UpgradePlanStatus): {
  title: string;
  variant: 'success' | 'error' | 'warning' | 'neutral' | 'brand';
} {
  switch (status) {
    case 'pending':
      return {
        title: 'Waiting for Plan',
        variant: 'neutral',
      };
    case 'in-progress':
      return {
        title: 'In Progress',
        variant: 'brand',
      };
    case 'completed':
      return {
        title: 'Completed',
        variant: 'success',
      };
    case 'skipped':
      return {
        title: 'Skipped',
        variant: 'neutral',
      };
    case 'rolled-back':
      return {
        title: 'Rolled Back',
        variant: 'warning',
      };
    case 'failed':
      return {
        title: 'Failed',
        variant: 'error',
      };
    case 'cancellation-in-progress':
      return {
        title: 'Cancelation in Progress',
        variant: 'error',
      };
    case 'cancelled':
      return {
        title: 'Canceled',
        variant: 'error',
      };
    case 'outdated':
      return {
        title: 'Outdated',
        variant: 'brand',
      };
    case 'update-in-progress':
      return {
        title: 'Refreshing Plan',
        variant: 'brand',
      };
    case 'archived':
      return {
        title: 'Archived',
        variant: 'neutral',
      };
  }
}

export function getUpgradeAssessmentStatusBadge(
  status: UpgradeAssessmentStatus,
): {
  title: string;
  variant: 'success' | 'error' | 'warning' | 'neutral' | 'brand';
  tooltip?: string;
} {
  switch (status) {
    case 'pending':
      return {
        title: 'Waiting for Assessment',
        variant: 'neutral',
        tooltip:
          'Upgrade Assessment generation can take up to 20 minutes. You will be notified when the Upgrade Assessment is published.',
      };
    case 'in-progress':
      return {
        title: 'Waiting for Assessment',
        variant: 'neutral',
        tooltip:
          'Upgrade Assessment generation can take up to 20 minutes. You will be notified when the Upgrade Assessment is published.',
      };
    case 'ready':
      return {
        title: 'Available',
        variant: 'success',
      };
    case 'failed':
      return {
        title: 'Failed',
        variant: 'error',
      };
    case 'outdated':
      return {
        title: 'Outdated',
        variant: 'warning',
      };
    case 'archived':
      return {
        title: 'Archived',
        variant: 'neutral',
      };
  }
}

export const getSeeMoreCommentsText = (
  seeMoreOpen: boolean,
  countToShow: number,
) => {
  if (seeMoreOpen) {
    return 'See less...';
  } else {
    return `See ${countToShow} more Comment${countToShow > 1 ? 's' : ''}...`;
  }
};

export type UpgradeAny = UpgradePlan | UpgradeTemplate | UpgradeAssessment;

export enum UpgradeViewMode {
  Plan,
  Template,
  Assessment,
}

export function getUpgradeModeHumanReadable(mode: UpgradeViewMode): string {
  switch (mode) {
    case UpgradeViewMode.Plan:
      return 'Upgrade Plan';
    case UpgradeViewMode.Template:
      return 'Upgrade Template';
    case UpgradeViewMode.Assessment:
      return 'Upgrade Assessment';
  }
}
